import { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import Checkbox from '@/components/ui/Checkbox';
import EditEventSeriesModal from '@/components/admin/events/EditEventSeriesModal';
import { faRepeat, faSpinner, faEdit, faTrash, faArrowsRotate, faExternalLinkAlt, faBell } from '@/fontawesome';
import { useUser } from '@/contexts/UserContext';
import { handleAPIError, showSuccessToast } from '@/utils/toasts';
import { formatRFCDate } from '@/utils/helpers';
import { describeRecurrenceRule, formatEventDateTime, formatReminderOffsetList } from '@/utils/event-helpers';
import { APIRoutes } from '@/services/api.routes';
import type { Event, EventSeries, EventSeriesID, EventSeriesSortField, FeatureType } from '@/models';
import { FEATURE_TYPE_LABELS } from '@/models';

const pageSize = 50;

const SORT_OPTIONS: { value: EventSeriesSortField; label: string }[] = [
  { value: 'start',   label: 'Serienstart' },
  { value: 'subject', label: 'Betreff' },
  { value: 'created', label: 'Erstellt' },
  { value: 'updated', label: 'Aktualisiert' },
];

// Rendered inside the "Serien" tab of AdminEventsList. Admin-only: every /event-series
// endpoint requires PermAdmin, employees get a 403.
interface EventSeriesTableProps {
  onEventsChanged: () => void; // series mutations create/delete events, so the events tab is stale afterwards
  refreshKey: number;          // bumped by the parent after a series was created
}

export default function EventSeriesTable({ onEventsChanged, refreshKey }: EventSeriesTableProps) {
  const { isAdmin } = useUser();
  const [series, setSeries] = useState<EventSeries[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeOnly, setActiveOnly] = useState(true);
  const [sort, setSort] = useState<EventSeriesSortField>('start');
  const [editingSeries, setEditingSeries] = useState<EventSeries | null>(null);
  const [busySeries, setBusySeries] = useState<EventSeriesID | null>(null);

  const fetchSeries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Generate cursor token for page-based navigation
      const cursorToken = currentPage > 1 ? `$${currentPage}` : undefined;

      const response = await APIRoutes.listEventSeries({
        pageSize: pageSize,
        next: cursorToken,
        activeOnly: activeOnly ? true : undefined,
        sort: sort
      });
      setSeries(response.series);
      setTotal(response.total);
    } catch (err) {
      console.error('Error loading event series:', err);
      setError('Die Event-Serien konnten nicht geladen werden');
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, activeOnly, sort, refreshKey]);

  useEffect(() => {
    void fetchSeries();
  }, [fetchSeries]);

  const handleSeriesUpdated = (updated: EventSeries, _events: Event[], regenerated: boolean) => {
    setSeries(prev => prev.map(s => (s.id === updated.id ? updated : s)));
    showSuccessToast(
      regenerated
        ? 'Serie gespeichert — zukünftige Termine wurden neu aufgebaut'
        : 'Serie gespeichert'
    );
    if (regenerated) onEventsChanged();
  };

  const handleRegenerate = async (s: EventSeries) => {
    if (!confirm(`Alle zukünftigen Termine von „${s.subject}" werden gelöscht und aus der aktuellen Regel neu erzeugt. Einzeln angepasste Termine verlieren dabei ihre Anpassungen. Vergangene Termine bleiben erhalten.\n\nFortfahren?`)) {
      return;
    }

    try {
      setBusySeries(s.id);
      const response = await APIRoutes.regenerateEventSeries(s.id);
      setSeries(prev => prev.map(item => (item.id === response.series.id ? response.series : item)));
      showSuccessToast(`${response.events.length} Termin(e) neu erzeugt`);
      onEventsChanged();
    } catch (err) {
      console.error('Error regenerating event series:', err);
      handleAPIError(err, 'Fehler beim Neuaufbau der Serie');
    } finally {
      setBusySeries(null);
    }
  };

  const handleDelete = async (s: EventSeries) => {
    if (!confirm(`Serie „${s.subject}" wirklich löschen? Zukünftige Termine werden dabei entfernt.`)) {
      return;
    }

    // Past occurrences are kept by default so the calendar history stays intact
    const includePast = confirm('Sollen auch die bereits vergangenen Termine gelöscht werden?\n\nOK = auch Vergangenheit löschen\nAbbrechen = Vergangenheit behalten');

    try {
      setBusySeries(s.id);
      await APIRoutes.deleteEventSeries(s.id, includePast ? true : undefined);
      setSeries(prev => prev.filter(item => item.id !== s.id));
      setTotal(prev => prev - 1);
      showSuccessToast('Serie gelöscht');
      onEventsChanged();
    } catch (err) {
      console.error('Error deleting event series:', err);
      handleAPIError(err, 'Fehler beim Löschen der Serie');
    } finally {
      setBusySeries(null);
    }
  };

  const renderAudience = (s: EventSeries) => {
    if (s.featureFilter === null) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
          Öffentlich
        </span>
      );
    }

    const features: FeatureType[] = s.featureFilter.features ?? [];

    return (
      <div className="flex flex-wrap gap-1">
        {features.map((f) => (
          <span
            key={f}
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
          >
            {FEATURE_TYPE_LABELS[f]}
          </span>
        ))}
        {s.featureFilter.onlyCommunityMembers && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            nur Community
          </span>
        )}
        {s.featureFilter.excludeCommunityMembers && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
            ohne Community
          </span>
        )}
        {features.length === 0 && !s.featureFilter.onlyCommunityMembers && !s.featureFilter.excludeCommunityMembers && (
          <span className="text-sm text-gray-500 dark:text-gray-400">-</span>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Filter Section */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md border border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap items-center gap-4">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter:</span>

          <Checkbox
            checked={activeOnly}
            onChange={(checked) => {
              setActiveOnly(checked);
              setCurrentPage(1);
            }}
            colorTheme="purple"
            label="Nur laufende Serien"
          />

          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-700 dark:text-gray-300">Sortierung:</label>
            <select
              value={sort}
              onChange={(e) => {
                setSort(e.target.value as EventSeriesSortField);
                setCurrentPage(1);
              }}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-purple-500 focus:border-purple-500 cursor-pointer"
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-12">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-purple-600 dark:text-purple-400 animate-spin" />
        </div>
      )}

      {error !== null && (
        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-6">
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={() => void fetchSeries()}
            className="mt-2 text-red-600 dark:text-red-400 underline hover:text-red-800 dark:hover:text-red-300 cursor-pointer"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      {!loading && error === null && series.length === 0 && (
        <div className="text-center py-12">
          <FontAwesomeIcon icon={faRepeat} className="w-16 h-16 text-gray-400 dark:text-gray-600 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Keine Event-Serien gefunden
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Legen Sie eine Serie an, um wiederkehrende Termine automatisch zu erzeugen.
          </p>
        </div>
      )}

      {!loading && series.length > 0 && (
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Betreff
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Erster Termin
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Letzter Termin
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Bis
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Termine
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Zielgruppe
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Erinnerungen
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Link
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {series.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="px-6 py-4">
                      <Link
                        to={`/admin/event-series/${s.id}`}
                        className="text-sm font-medium text-purple-600 dark:text-purple-400 hover:underline"
                      >
                        {s.subject}
                      </Link>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {describeRecurrenceRule(s.rule)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {s.firstOccurrence !== null ? formatEventDateTime(s.firstOccurrence) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {s.lastOccurrence !== null ? formatEventDateTime(s.lastOccurrence) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatRFCDate(s.until)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {s.occurrenceCount}
                    </td>
                    <td className="px-6 py-4">
                      {renderAudience(s)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(s.reminderMinutes ?? []).length > 0 ? (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                          title={`Erinnerungsmails ${formatReminderOffsetList(s.reminderMinutes ?? [])} vor Beginn`}
                        >
                          <FontAwesomeIcon icon={faBell} className="w-3 h-3 mr-1" />
                          {formatReminderOffsetList(s.reminderMinutes ?? [])}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-600">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {s.link.length > 0 && (
                        <a
                          href={s.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 inline-flex items-center"
                          title={s.link}
                        >
                          <FontAwesomeIcon icon={faExternalLinkAlt} className="w-4 h-4" />
                        </a>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => setEditingSeries(s)}
                          disabled={!isAdmin || busySeries === s.id}
                          className={`text-blue-600 dark:text-blue-400 disabled:opacity-50 ${!isAdmin ? 'cursor-not-allowed' : 'hover:text-blue-900 dark:hover:text-blue-300 cursor-pointer'}`}
                          title={isAdmin ? 'Bearbeiten' : 'Nur für Administratoren'}
                        >
                          <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => void handleRegenerate(s)}
                          disabled={!isAdmin || busySeries === s.id}
                          className={`text-amber-600 dark:text-amber-400 disabled:opacity-50 ${!isAdmin ? 'cursor-not-allowed' : 'hover:text-amber-800 dark:hover:text-amber-300 cursor-pointer'}`}
                          title={isAdmin ? 'Zukünftige Termine neu aufbauen' : 'Nur für Administratoren'}
                        >
                          <FontAwesomeIcon icon={faArrowsRotate} className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => void handleDelete(s)}
                          disabled={!isAdmin || busySeries === s.id}
                          className={`text-red-600 dark:text-red-400 disabled:opacity-50 ${!isAdmin ? 'cursor-not-allowed' : 'hover:text-red-900 dark:hover:text-red-300 cursor-pointer'}`}
                          title={isAdmin ? 'Löschen' : 'Nur für Administratoren'}
                        >
                          {busySeries === s.id ? (
                            <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                          ) : (
                            <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <PageBasedPagination
            currentPage={currentPage}
            totalCount={total}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            loading={loading}
          />
        </div>
      )}

      {editingSeries !== null && (
        <EditEventSeriesModal
          isOpen={true}
          onClose={() => setEditingSeries(null)}
          series={editingSeries}
          onSeriesUpdated={handleSeriesUpdated}
        />
      )}
    </>
  );
}
